/**
 * useRole — 前端 RBAC 集中 hook（docs/rbac.md §5.2）。
 *
 * 设计要点：
 *  - 数据源唯一：`spaceApi.getRole()` 走 GET /api/v1/spaces/role，
 *    不再让每个页面各自内联 useEffect；
 *  - 401（未登录）→ 视为 `anonymous`，**不重试**（refresh middleware
 *    已尝试一次 refresh，失败说明确实未登录）；
 *  - 30s staleTime 避免每次导航重拉；
 *  - 返回 RoleState 联合：`'loading' | 'anonymous' | 'owner' | 'member'`，
 *    让上层 RequireRole / 业务页面能区分"还在拉"和"已知是匿名"。
 */

import { useQuery } from '@tanstack/react-query';
import { spaceApi } from '../../features/admin/api/space';
import { ApiError } from '../api/apiError';

export type Role = 'owner' | 'member';
export type RoleState = 'loading' | 'anonymous' | Role;

export interface UseRoleResult {
  role: RoleState;
  isLoading: boolean;
  isOwner: boolean;
  isMember: boolean;
  isAuthenticated: boolean;
  error: Error | null;
}

interface RoleQueryData {
  role: Role | null;
}

export function useRole(): UseRoleResult {
  const query = useQuery<RoleQueryData, Error>({
    queryKey: ['role', 'me'],
    queryFn: async (): Promise<RoleQueryData> => {
      try {
        const roleResp = await spaceApi.getRole();
        return { role: roleResp.role as Role };
      } catch (err: unknown) {
        // 401 → 匿名：refresh middleware 已经尝试一次 refresh，
        // 仍 401 表示当前确实未登录。
        if (err instanceof ApiError && err.http === 401) {
          return { role: null };
        }
        throw err;
      }
    },
    staleTime: 30_000,
    retry: 1,
  });

  const role = query.data?.role ?? null;
  const isLoading = query.isLoading;
  return {
    role: isLoading ? 'loading' : (role ?? 'anonymous'),
    isLoading,
    isOwner: role === 'owner',
    isMember: role === 'member',
    isAuthenticated: role !== null,
    error: query.error ?? null,
  };
}